import React, { useRef, useState } from "react";
import { ChevronDown, FilePlus2, FileEdit, Loader2, Megaphone } from "lucide-react";
import SignalsAndConsensus from "./SignalsAndConsensus";
import { CreateContentList, OptimizeContentList } from "./OwnSiteRecommendations";
import EarnedMediaGrid from "./EarnedMediaGrid";
import { isCurrentStrategyShape } from "./strategyShape";
import AiVisibilityCard from "./sections/AiVisibilityCard";
import OpportunityScoreCard from "./sections/OpportunityScoreCard";
import ProviderConsensusCard from "./sections/ProviderConsensusCard";
import PromptDetailsCard from "./sections/PromptDetailsCard";
import ResearchFootprintCard from "./sections/ResearchFootprintCard";
import CompetitorConsensusCard from "./sections/CompetitorConsensusCard";

function SectionLabel({ icon, children }) {
	return (
		<h5 className="monitoring__analysis-strategy-section-title d-flex align-items-center gap-2 mb-1">
			{icon}
			{children}
		</h5>
	);
}

function StrategySectionAccordion({ title, children }) {
	const [open, setOpen] = useState(false);

	return (
		<div className="monitoring__analysis-strategy-details">
			<button
				type="button"
				className="monitoring__analysis-strategy-details-toggle"
				onClick={() => setOpen((prev) => !prev)}
				aria-expanded={open}
			>
				<span>{title}</span>
				<ChevronDown
					size={16}
					className={`monitoring__analysis-strategy-details-chevron${
						open ? " is-open" : ""
					}`}
					aria-hidden
				/>
			</button>
			{open ? (
				<div className="monitoring__analysis-strategy-details-body">{children}</div>
			) : null}
		</div>
	);
}

function AnalysisStrategyPendingView({ onViewFanout }) {
	return (
		<div className="monitoring__analysis-strategy-pending">
			<div className="monitoring__analysis-strategy-pending-glow" aria-hidden />
			<div className="monitoring__analysis-strategy-pending-inner">
				<div className="monitoring__analysis-strategy-pending-icon">
					<Loader2 size={28} className="monitoring__analysis-spinner" aria-hidden />
				</div>
				<p className="monitoring__analysis-strategy-pending-kicker mb-1">
					Content strategy
				</p>
				<h4 className="monitoring__analysis-strategy-pending-title mb-2">
					Generating content strategy…
				</h4>
				<p className="monitoring__analysis-strategy-pending-desc mb-3">
					Fan-out analysis is ready. We&apos;re consolidating citations and
					recommendations across providers - usually 1–2 minutes.
				</p>
				{onViewFanout && (
					<button
						type="button"
						className="btn btn-sm btn-outline-primary"
						onClick={onViewFanout}
					>
						View fan-out while you wait
					</button>
				)}
			</div>
		</div>
	);
}

export default function AnalysisStrategyView({
	strategy,
	strategyError,
	strategyPending = false,
	onViewGapAnalysis,
	websiteId,
	analysisId,
	onOpenWritingRun,
	brandName,
	brandDomain,
}) {
	const [activeSection, setActiveSection] = useState("own_site");
	const ownSiteRef = useRef(null);
	const earnedMediaRef = useRef(null);

	if (strategyError) {
		return (
			<div className="monitoring__analysis-error small">
				Content strategy generation did not complete: {strategyError}. The fan-out
				analysis above is unaffected.
			</div>
		);
	}
	if (!strategy) {
		if (strategyPending) {
			return <AnalysisStrategyPendingView onViewFanout={onViewGapAnalysis} />;
		}
		return <p className="text-muted small mb-0">No content strategy available.</p>;
	}
	if (!isCurrentStrategyShape(strategy)) {
		return (
			<p className="text-muted small mb-0">
				This analysis was generated before the Content Strategy tab&apos;s latest update
				and can&apos;t be displayed. Click{" "}
				<span className="monitoring__analysis-strategy-rerun-action">&ldquo;Analyse&rdquo;</span>{" "}
				again on this prompt to regenerate it with the current view.
			</p>
		);
	}

	const createItems = strategy.recommendations.own_site.create;
	const optimizeItems = strategy.recommendations.own_site.optimize;
	const externalItems = strategy.recommendations.external ?? [];
	const ownSiteCount = createItems.length + optimizeItems.length;

	function scrollToSection(section, ref) {
		setActiveSection(section);
		ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });
	}

	return (
		<div className="monitoring__analysis-strategy-v2">
			<div className="monitoring__analysis-strategy-rows">
				<div className="monitoring__analysis-strategy-row monitoring__analysis-strategy-row--summary">
					<AiVisibilityCard strategy={strategy} />
					<OpportunityScoreCard strategy={strategy} />
				</div>

				<div className="monitoring__analysis-strategy-row monitoring__analysis-strategy-row--recommendations">
					<div className="monitoring__analysis-strategy-card monitoring__analysis-strategy-recommendations">
						<div className="monitoring__analysis-strategy-rec-head">
							<div>
								<h4 className="monitoring__analysis-strategy-section-heading mb-1">
									Recommendations
								</h4>
								<p className="small text-muted mb-0">
									Actionable steps to improve your visibility across all AI providers.
								</p>
							</div>
							<div
								className="monitoring__segmented monitoring__segmented--sm monitoring__analysis-strategy-jump-nav"
								role="group"
								aria-label="Recommendation sections"
							>
								<button
									type="button"
									className={`monitoring__segmented-btn${
										activeSection === "own_site" ? " is-active" : ""
									}`}
									onClick={() => scrollToSection("own_site", ownSiteRef)}
									aria-pressed={activeSection === "own_site"}
								>
									Own Site ({ownSiteCount})
								</button>
								<button
									type="button"
									className={`monitoring__segmented-btn${
										activeSection === "earned_media" ? " is-active" : ""
									}`}
									onClick={() => scrollToSection("earned_media", earnedMediaRef)}
									aria-pressed={activeSection === "earned_media"}
								>
									Earned Media ({externalItems.length})
								</button>
							</div>
						</div>

						<div className="monitoring__analysis-strategy-rec-body">
							<div ref={ownSiteRef} className="monitoring__analysis-strategy-rec-section">
								<SectionLabel
									icon={<FilePlus2 size={14} className="text-success" aria-hidden />}
								>
									Create New Content ({createItems.length})
								</SectionLabel>
								<p className="small text-muted mb-2">
									Build new pages targeting the most valuable topics.
								</p>
								<CreateContentList
									items={createItems}
									crossProviderSignals={strategy.cross_provider_signals}
									websiteId={websiteId}
									analysisId={analysisId}
									onOpenWritingRun={onOpenWritingRun}
								/>
							</div>

							<div className="monitoring__analysis-strategy-rec-section border-top pt-3 mt-3">
								<SectionLabel
									icon={<FileEdit size={14} className="text-primary" aria-hidden />}
								>
									Optimize Existing Content ({optimizeItems.length})
								</SectionLabel>
								<p className="small text-muted mb-2">
									Improve existing pages to close content gaps.
								</p>
								<OptimizeContentList
									items={optimizeItems}
									crossProviderSignals={strategy.cross_provider_signals}
									onViewGapAnalysis={onViewGapAnalysis}
									websiteId={websiteId}
									analysisId={analysisId}
									onOpenWritingRun={onOpenWritingRun}
								/>
							</div>

							<div
								ref={earnedMediaRef}
								className="monitoring__analysis-strategy-rec-section border-top pt-3 mt-3"
							>
								<SectionLabel
									icon={<Megaphone size={14} className="text-purple" aria-hidden />}
								>
									Earned Media Recommendations ({externalItems.length})
								</SectionLabel>
								<p className="small text-muted mb-2">
									Build authority and external signals that influence AI recommendations.
								</p>
								<EarnedMediaGrid items={externalItems} />
							</div>
						</div>
					</div>
				</div>

				<div className="monitoring__analysis-strategy-row monitoring__analysis-strategy-row--details">
					<div className="monitoring__analysis-strategy-details-stack">
						<StrategySectionAccordion
							title={`Cross-Provider Winning Signals (${strategy.cross_provider_signals.length})`}
						>
							<SignalsAndConsensus strategy={strategy} embedded />
						</StrategySectionAccordion>
						<StrategySectionAccordion title="Prompt Details">
							<PromptDetailsCard strategy={strategy} embedded />
						</StrategySectionAccordion>
						<StrategySectionAccordion title="Competitor Consensus">
							<CompetitorConsensusCard
								strategy={strategy}
								brandName={brandName}
								brandDomain={brandDomain}
								embedded
							/>
						</StrategySectionAccordion>
						<StrategySectionAccordion title="Provider Consensus">
							<ProviderConsensusCard strategy={strategy} embedded />
						</StrategySectionAccordion>
						<StrategySectionAccordion title="AI Research Footprint">
							<ResearchFootprintCard
								strategy={strategy}
								onViewDetails={onViewGapAnalysis}
								embedded
							/>
						</StrategySectionAccordion>
					</div>
				</div>
			</div>
		</div>
	);
}
